/**
 * Tile orientation in a gid. Tiled stores it in the top three bits: horizontal flip, vertical
 * flip and a diagonal flip (transpose). Applied in that order of meaning: diagonal first, then
 * horizontal, then vertical. A gid's orientation is one of the 8 flips/quarter-turns of a square.
 */
export const FLIP_H = 0x80000000;
export const FLIP_V = 0x40000000;
export const FLIP_D = 0x20000000;
const FLAGS = FLIP_H | FLIP_V | FLIP_D;

/** 2x2 matrix [a, b, c, d] = [[a, b], [c, d]], y pointing down. */
type Matrix = readonly [number, number, number, number];

function multiply(p: Matrix, q: Matrix): Matrix {
  return [
    p[0] * q[0] + p[1] * q[2],
    p[0] * q[1] + p[1] * q[3],
    p[2] * q[0] + p[3] * q[2],
    p[2] * q[1] + p[3] * q[3],
  ];
}

const IDENTITY: Matrix = [1, 0, 0, 1];
const TRANSPOSE: Matrix = [0, 1, 1, 0];
/** Quarter turn clockwise on screen. */
const TURN_CW: Matrix = [0, -1, 1, 0];
const MIRROR_X: Matrix = [-1, 0, 0, 1];

function toMatrix(gid: number): Matrix {
  let m = IDENTITY;
  if (gid & FLIP_D) m = multiply(TRANSPOSE, m);
  if (gid & FLIP_H) m = multiply(MIRROR_X, m);
  if (gid & FLIP_V) m = multiply([1, 0, 0, -1], m);
  return m;
}

const FLAG_CHOICES = [
  0,
  FLIP_H,
  FLIP_V,
  FLIP_H | FLIP_V,
  FLIP_D,
  FLIP_D | FLIP_H,
  FLIP_D | FLIP_V,
  FLAGS,
];

function withMatrix(gid: number, m: Matrix): number {
  const id = gid & ~FLAGS;
  for (const flags of FLAG_CHOICES) {
    const c = toMatrix(flags);
    if (c.every((v, i) => v === m[i])) return (id | flags) >>> 0;
  }
  return gid; // unreachable: the 8 flag combinations cover every flip and quarter turn
}

/** The gid turned a quarter clockwise (in place; `0` stays `0`). */
export function turnGid(gid: number): number {
  return gid === 0 ? 0 : withMatrix(gid, multiply(TURN_CW, toMatrix(gid)));
}

/** The gid mirrored left to right (in place). */
export function mirrorGid(gid: number): number {
  return gid === 0 ? 0 : withMatrix(gid, multiply(MIRROR_X, toMatrix(gid)));
}

/**
 * How to draw a gid's picture: turn it `angle` degrees clockwise (0/90/180/270) after mirroring
 * it left-right and/or top-bottom first (scale -1). Suits canvas and Phaser alike.
 */
export function gidTransform(gid: number): { angle: number; flipX: boolean; flipY: boolean } {
  const m = toMatrix(gid);
  let turn: Matrix = IDENTITY;
  for (const angle of [0, 90, 180, 270]) {
    for (const flipX of [false, true]) {
      for (const flipY of [false, true]) {
        const c = multiply(turn, [flipX ? -1 : 1, 0, 0, flipY ? -1 : 1]);
        if (c.every((v, i) => v === m[i])) return { angle, flipX, flipY };
      }
    }
    turn = multiply(TURN_CW, turn);
  }
  return { angle: 0, flipX: false, flipY: false };
}

/** Rows of gids turned a quarter clockwise as a picture: tiles move and each tile turns too. */
export function turnRows(rows: readonly (readonly number[])[]): number[][] {
  const height = rows.length;
  const width = rows[0]?.length ?? 0;
  const out: number[][] = [];
  for (let y = 0; y < width; y++) {
    const row: number[] = [];
    for (let x = 0; x < height; x++) row.push(turnGid(rows[height - 1 - x]?.[y] ?? 0));
    out.push(row);
  }
  return out;
}

/** Rows of gids mirrored left to right as a picture. */
export function mirrorRows(rows: readonly (readonly number[])[]): number[][] {
  return rows.map((row) => [...row].reverse().map(mirrorGid));
}
